import React, { useContext, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiHeart, FiSearch, FiX } from "react-icons/fi";
import "../css/shop.scss";
import productData, {
  formatCurrency,
  siteConfig,
} from "../Datas/productData";
import { CartContext } from "./CartContext";

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toggleWishlist, isInWishlist } = useContext(CartContext);
  const activeCategory = searchParams.get("category") || "All";
  const searchTerm = searchParams.get("q") || "";
  const activeTag = searchParams.get("tag") || "";
  const priceFilter = searchParams.get("price") || "";
  const saleOnly = searchParams.get("sale") === "true";
  const sort = searchParams.get("sort") || "featured";

  const visibleProducts = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    const filtered = productData.filter((product) => {
      const categoryMatches =
        activeCategory === "All" || product.category === activeCategory;
      const tagMatches = !activeTag || product.tags?.includes(activeTag);
      const priceMatches = priceFilter !== "under-15" || product.price < 15;
      const saleMatches = !saleOnly || product.tags?.includes("sale");
      const searchableText = [
        product.name,
        product.category,
        ...(product.tags || []),
      ]
        .join(" ")
        .toLowerCase();
      return (
        categoryMatches &&
        tagMatches &&
        priceMatches &&
        saleMatches &&
        searchableText.includes(normalizedTerm)
      );
    });

    return [...filtered].sort((a, b) => {
      if (sort === "price-asc") return a.price - b.price;
      if (sort === "price-desc") return b.price - a.price;
      if (sort === "name-asc") return a.name.localeCompare(b.name);
      if (sort === "name-desc") return b.name.localeCompare(a.name);
      if (sort === "newest") return b.id - a.id;
      if (sort === "oldest") return a.id - b.id;
      if (sort === "best-selling") {
        return (
          Number(b.tags?.includes("bestseller")) -
            Number(a.tags?.includes("bestseller")) ||
          a.id - b.id
        );
      }
      return a.id - b.id;
    });
  }, [activeCategory, activeTag, priceFilter, saleOnly, searchTerm, sort]);

  const collectionTitle =
    activeCategory !== "All"
      ? activeCategory
      : activeTag === "new"
      ? "New Arrivals"
      : activeTag === "kids"
      ? "For Kids"
      : saleOnly
      ? "Sale"
      : searchTerm
      ? "Search Results"
      : "Shop";

  useEffect(() => {
    document.title = `${collectionTitle} — ${siteConfig.brandName}`;
  }, [collectionTitle]);

  const updateParam = (key, value, defaultValue) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) nextParams.delete(key);
    else nextParams.set(key, value);
    setSearchParams(nextParams);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearFilters = () => setSearchParams({});

  return (
    <main className="shop-page" id="main-content">
      <header className="collection-heading">
        <span aria-hidden="true" />
        <h1>{collectionTitle}</h1>
        <span aria-hidden="true" />
      </header>

      <div className="shop-layout">
        <section className="shop-results" aria-live="polite">
          <div className="shop-toolbar">
            <div className="shop-toolbar__summary">
              <p className="shop-toolbar__count">
                {visibleProducts.length}{" "}
                {visibleProducts.length === 1 ? "item" : "items"}
              </p>
              {(activeCategory !== "All" ||
                activeTag ||
                priceFilter ||
                saleOnly ||
                searchTerm) && (
                <button type="button" onClick={clearFilters}>
                  View all
                </button>
              )}
            </div>

            <div className="shop-toolbar__sort">
              <label htmlFor="sort-products">Sort by</label>
              <select
                id="sort-products"
                value={sort}
                onChange={(event) =>
                  updateParam("sort", event.target.value, "featured")
                }
              >
                <option value="featured">Featured</option>
                <option value="best-selling">Best selling</option>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="price-asc">Price, low to high</option>
                <option value="price-desc">Price, high to low</option>
                <option value="name-asc">Alphabetically, A–Z</option>
                <option value="name-desc">Alphabetically, Z–A</option>
              </select>
            </div>
          </div>

          {searchTerm && (
            <div className="shop-query">
              <FiSearch aria-hidden="true" />
              <span>
                Results for <strong>“{searchTerm}”</strong>
              </span>
              <button
                type="button"
                onClick={() => updateParam("q", "", "")}
                aria-label="Clear search"
              >
                <FiX />
              </button>
            </div>
          )}

          {visibleProducts.length > 0 ? (
            <div className="product-grid">
              {visibleProducts.map((product) => (
                <article className="product-card" key={product.id}>
                  <Link
                    className="product-card__image"
                    to={`/shopDetails/${product.id}`}
                  >
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      loading="lazy"
                    />
                    {product.tags?.includes("new") && (
                      <span className="product-card__badge">New</span>
                    )}
                  </Link>
                  <button
                    className={`product-card__wish ${
                      isInWishlist(product.id) ? "is-active" : ""
                    }`}
                    type="button"
                    onClick={() => toggleWishlist(product.id)}
                    aria-pressed={isInWishlist(product.id)}
                    aria-label={`${
                      isInWishlist(product.id) ? "Remove" : "Add"
                    } ${product.name} ${
                      isInWishlist(product.id) ? "from" : "to"
                    } wishlist`}
                  >
                    <FiHeart />
                  </button>
                  <div className="product-card__copy">
                    <p className="product-card__category">{product.category}</p>
                    <Link to={`/shopDetails/${product.id}`}>
                      {product.name}
                    </Link>
                    <p className="product-card__price">
                      {formatCurrency(product.price)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="shop-empty">
              <FiSearch aria-hidden="true" />
              <h2>No paper goods found</h2>
              <p>Try a different search or browse the complete collection.</p>
              <button className="button" type="button" onClick={clearFilters}>
                View all products
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

export default Shop;
